import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { usePriceChange } from 'components/station/components/competitor-pricing/context/priceChangeContext'
import {
  PriceChangeStatus,
  PriceChangeActions,
} from 'components/station/components/competitor-pricing/context/constants'
import { ActionAtSelector } from 'components/common/price-change-footer/components/action-at-selector'
import { Reminders } from 'components/common/price-change-footer/components/reminders'
import { PriceChangeTimeout } from 'utils/pricing/constants'
import classNames from 'classnames/bind'
import { useCreatePolePriceChangeRequests } from './hooks/useCreatePolePriceChangeRequests'
import { ProgressBar } from './components/progress-bar'
import { Cover } from './components/cover'
import { StatusIndicator } from './components/status-indicator'
import styles from './PriceChangeFooter.module.scss'
import { HowToUpdatePrice } from 'components/common/price-change-footer/components/action-at-selector/components/how-to-update'

const cx = classNames.bind(styles)

const onError = dispatch => () => {
  dispatch({
    type: PriceChangeActions.SetCurrentStatus,
    payload: { status: PriceChangeStatus.Error },
  })
}

const onCompleted = dispatch => ({ createPolePriceChangeRequests }) => {
  const status = createPolePriceChangeRequests.some(({ error }) => error)
    ? PriceChangeStatus.Error
    : PriceChangeStatus.Sent
  dispatch({
    type: PriceChangeActions.SetCurrentStatus,
    payload: { status },
  })
}

const PriceChangeFooter = ({
  stationId,
  stationName,
  setDateTimeSelectorInUse,
  supportsPriceExecution
}) => {
  const [state, dispatch] = usePriceChange()
  const { t } = useTranslation()
  const [
    createPolePriceChangeRequests,
    loading,
    called,
  ] = useCreatePolePriceChangeRequests(
    stationId,
    onError(dispatch),
    onCompleted(dispatch)
  )

  const {
    status,
    reminders,
    priceChangeMethod,
    actionAt: { selectedPeriod, selectedDate, selectedTime },
  } = state

  useEffect(() => {
    let timer

    if (status === PriceChangeStatus.Send && !called) {
      timer = setTimeout(() => {
        createPolePriceChangeRequests()
      }, PriceChangeTimeout)
    }

    return () => {
      clearTimeout(timer)
    }
  }, [status, called, createPolePriceChangeRequests])

  const handleReminderToggle = reminder => {
    /* istanbul ignore next */
    if (!loading) {
      dispatch({
        type: PriceChangeActions.ToggleReminder,
        payload: { reminder },
      })
    }
  }

  const handleConfirmClick = (selectedPeriod, selectedDate, selectedTime) => {
    dispatch({
      type: PriceChangeActions.SetActionAt,
      payload: { selectedPeriod, selectedDate, selectedTime },
    })
  }

  const handlePriceChangeMethodToggle = (priceChangeMethod) => {
    if (loading || !supportsPriceExecution) return // Prevent toggling if loading or station doesn't support price execution

    const { sendToPos, notifyStationManager } = state.priceChangeMethod

    const isOnlySendToPos = sendToPos && !notifyStationManager
    const isOnlyNotifyManager = !sendToPos && notifyStationManager

    // If only notifyStationManager is on and user is toggling it off, toggle both
    // If only sendToPos is on and user is toggling it off, toggle both
    const shouldToggleBoth =
      (isOnlyNotifyManager && priceChangeMethod === 'notifyStationManager') ||
      (isOnlySendToPos && priceChangeMethod === 'sendToPos')

    dispatch({
      type: PriceChangeActions.SetPriceChangeMethod,
      payload: {
        priceChangeMethod: shouldToggleBoth ? ['sendToPos', 'notifyStationManager'] : [priceChangeMethod],
      },
    })
  }

  const { sendToPos } = priceChangeMethod

  return (
    <div className={cx('price-change-footer')}>
      <ProgressBar status={status} />
      <Cover status={status} />
      <span className={cx('title')}>{t('widgets.stationsListPricing.priceChangeOptions')}</span>
      <div className={cx('main-content')}>
        <ActionAtSelector
          selectedPeriod={selectedPeriod}
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          onConfirmClick={handleConfirmClick}
        />

        <HowToUpdatePrice
          priceChangeMethod={priceChangeMethod}
          onPriceChangeMethodToggle={handlePriceChangeMethodToggle}
          supportsPriceExecution={supportsPriceExecution}
        />

        <Reminders
          reminders={reminders}
          onReminderToggle={handleReminderToggle}
        />
      </div>
      <div className={cx('secondary-content')}>
        <div className={cx('station')}>
          <span>{t('widgets.competitorPricing.station')}</span>
          <span className={cx('station-name')}>{stationName}</span>
        </div>
        <StatusIndicator
          loading={loading}
          setDateTimeSelectorInUse={setDateTimeSelectorInUse}
          sendToPos={sendToPos}
        />
      </div>
    </div>
  )
}

PriceChangeFooter.propTypes = {
  stationId: PropTypes.string.isRequired,
  stationName: PropTypes.string.isRequired,
  setDateTimeSelectorInUse: PropTypes.func.isRequired,
  supportsPriceExecution: PropTypes.bool.isRequired,
}

export { PriceChangeFooter }
